import { CachedImageProvider } from '@bestseller/retail-planning.styletimeline';
import { BrandImage } from '@bestseller-bit/frontend-community.components.brand-image';
import { TooltipButton } from '@bestseller-bit/frontend-community.components.tooltip-button';
import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import { ArrowBack, Edit, Fullscreen, FullscreenExit, Warning } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    styled,
    Tooltip,
    Typography,
} from '@mui/material';
import { CellKeyDownEvent, GridApi, GridReadyEvent } from 'ag-grid-community';
import { differenceInDays, parseISO } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { FullScreenContainer } from 'src/components/atoms/FullScreenContainer/FullScreenContainer';
import Heading from 'src/components/atoms/Heading/Heading';
import { CountStatusBar } from 'src/components/molecules/CountStatusBar/CountStatusBar';
import { ActionButtons } from 'src/components/organisms/ActionButtons/ActionsButtons';
import SettingsDropdown from 'src/components/organisms/ActionButtons/SettingsDropdown';
import { DataTable } from 'src/components/organisms/DataTable/DataTable';
import { SumBold } from 'src/components/organisms/DataTable/options/sum-bold';
import { Cell } from 'src/components/organisms/DataTable/types';
import { TableWrapper } from 'src/components/styled/TableWrapper';
import { Module } from 'src/domain';
import { SalesCampaignOverviewRow } from 'src/domain/table/sales-campaign-overview.row';
import { StyleTimelineOverviewRow } from 'src/domain/table/style-timeline-overview-row';
import { useFilteredStores, useUnsavedChangesModule } from 'src/hooks';
import { useAuditLogDetails } from 'src/hooks/audit-log/useAuditLogDetails';
import { useDiscardChangesModule } from 'src/hooks/discard-changes/useDiscardChangesModule';
import { useSalesCampaignPlanningCellValuesChanged } from 'src/hooks/sales-campaigns-planning/useSalesCampaignPlanningCellValuesChanged';
import { useSalesCampaignPlanningSaveChanges } from 'src/hooks/sales-campaigns-planning/useSalesCampaignPlanningSaveChanges';
import { useSalesCampaignSimulationRows } from 'src/hooks/sales-campaigns-planning/useSalesCampaignSimulationRows';
import { useDiscardShortcut } from 'src/hooks/shortcuts/useDiscardShortcut';
import { useSaveShortcut } from 'src/hooks/shortcuts/useSaveShortcut';
import { useChangesSnackbar } from 'src/hooks/snackbar/useChangesSnackbar';
import { useStyleTimelineSaveChanges } from 'src/hooks/style-timeline/save-changes/useStyleTimelineSaveChanges';
import { useStorefrontTypesApiQuery } from 'src/hooks/style-timeline-settings/queries/useStorefrontTypesApiQuery';
import {
    CreateSalesCampaignInput,
    SalesCampaignModel,
    UpdateSalesCampaignInput,
} from 'src/infrastructure/rest-api/api-types';
import { useApiMutation } from 'src/infrastructure/rest-api/useApi';
import { isHTMLElement } from 'src/utils/dom';
import { formatStringDateTime } from 'src/utils/formatAsMonthYear';
import { SalesCampaignDialog } from '../SalesCampaigns/CreateCampaignDialog';
import { StyleTimeline } from '../StyleTimeline/StyleTimeline';
import { columns, defaultColDef } from './data/columns';

export const SalesCampaignsPlanning: FC = () => {
    const [fullscreen, setFullscreen] = useState(false);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const showSnackbar = useChangesSnackbar();

    const { id: salesCampaignId } = useParams();

    const {
        data: simulationRows,
        salesCampaign,
        loading: rowsLoading,
        error,
        refetchSalesCampaigns,
    } = useSalesCampaignSimulationRows(Number(salesCampaignId));

    const sortedColumns = useMemo(() => {
        return [...columns].sort((a, b) => {
            const aLabel = String(a.headerName ?? a.field ?? '');
            const bLabel = String(b.headerName ?? b.field ?? '');
            return aLabel.localeCompare(bLabel, undefined, { sensitivity: 'base' });
        });
    }, []);

    const lastPublishedDate = salesCampaign?.lastPublished ? parseISO(salesCampaign?.lastPublished) : null;

    const fetchAuditLogDetails = useAuditLogDetails(Module.SalesCampaigns);

    const showAuditLog = useCallback(() => {
        fetchAuditLogDetails({
            data: {
                id: Number(salesCampaignId),
            },
            colDefs: sortedColumns,
        });
    }, [fetchAuditLogDetails, salesCampaignId, sortedColumns]);

    const discardChangesSalesCampaigns = useDiscardChangesModule(Module.SalesCampaignsPlanning);
    const discardChangesStyleTimeline = useDiscardChangesModule(Module.StyleTimeline);
    const discardChanges = useCallback(() => {
        // Ensure the discards are queued in the correct order
        Promise.resolve()
            .then(() => discardChangesSalesCampaigns())
            .then(() => discardChangesStyleTimeline());
    }, [discardChangesSalesCampaigns, discardChangesStyleTimeline]);

    const [unsavedChangesSalesCampaignPlanning] = useUnsavedChangesModule<SalesCampaignOverviewRow[]>(
        Module.SalesCampaignsPlanning
    );
    const [unsavedChangesStyleTimeline] = useUnsavedChangesModule<StyleTimelineOverviewRow[]>(Module.StyleTimeline);

    const hasUnsavedChanges = !!unsavedChangesSalesCampaignPlanning || !!unsavedChangesStyleTimeline;

    const [saveChangesSalesCampaign, { isSaving: isSavingSalesCampaign }] = useSalesCampaignPlanningSaveChanges();
    const [saveChangesStyleTimeline, { isSaving: isSavingStyleTimeline }] = useStyleTimelineSaveChanges(
        salesCampaign?.storefrontType.name,
        { showSuccessSnackbar: false }
    );
    const isSaving = isSavingSalesCampaign || isSavingStyleTimeline;
    const saveChanges = useCallback(async () => {
        // Ensure the saves are queued in the correct order
        const styleTimelineSaved = await saveChangesStyleTimeline();
        if (!styleTimelineSaved) {
            return;
        }

        const salesCampaignSaved = await saveChangesSalesCampaign();
        if (salesCampaignSaved) {
            showSnackbar();
        }
    }, [saveChangesSalesCampaign, saveChangesStyleTimeline, showSnackbar]);

    const handleCellValuesChanged = useSalesCampaignPlanningCellValuesChanged();

    const [editableSalesCampaign, setEditableSalesCampaign] = useState<
        (UpdateSalesCampaignInput & CreateSalesCampaignInput) | null
    >();
    const { data: storefrontTypes, loading: storefrontTypesLoading } = useStorefrontTypesApiQuery();

    const loading = rowsLoading || storefrontTypesLoading;

    const [filteredStores] = useFilteredStores();
    const brandNumber = filteredStores?.[0]?.chain?.brandNumber;

    const handleCellKeyDown = useCallback(
        (event: CellKeyDownEvent) => {
            if (!event.event) {
                return;
            }

            const keyboardEvent = event.event as unknown as KeyboardEvent;

            const colId = event.column.getColId();
            const shouldHandle = ['discountPercentage', 'discount'].includes(colId) && !event.event.defaultPrevented;

            if (shouldHandle && ['Backspace', 'Clear', 'Delete'].includes(keyboardEvent.key)) {
                event.event.preventDefault();
                const changes: Cell<unknown>[] = [
                    {
                        value:
                            colId === 'discount'
                                ? {
                                      x: null,
                                      y: null,
                                      discountType: 'FIXED_PRICE',
                                  }
                                : null,
                        rowId: event.node.data['rowId'],
                        column: colId,
                        category: undefined,
                        footer: undefined,
                    },
                ];
                handleCellValuesChanged(changes);
            }
        },
        [handleCellValuesChanged]
    );

    const handleGridReady = useCallback((event: GridReadyEvent) => {
        setGridApi(event.api);
    }, []);

    const handleEditSalesCampaign = useCallback((salesCampaign: SalesCampaignModel) => {
        if (isHTMLElement(document.activeElement)) {
            // Blur the active element to prevent focus issues when opening the dialog
            document.activeElement.blur();
        }

        setEditableSalesCampaign({
            endDate: salesCampaign.endDate,
            salesCampaignId: salesCampaign.id,
            startDate: salesCampaign.startDate,
            name: salesCampaign.name,
            partnerCompositeId: 0,
            storefrontTypeId: salesCampaign.storefrontType?.id ?? 0,
        });
    }, []);

    const [publishLoading, setPublishLoading] = useState(false);
    const [publish] = useApiMutation('/api/salescampaigns/{id}/d365', 'put');
    const [showPublishDialog, setShowPublishDialog] = useState(false);
    const handlePublish = useCallback(() => {
        setPublishLoading(true);
        publish({
            path: {
                id: Number(salesCampaignId),
            },
        })
            .then(() => {
                showSnackbar(undefined, {
                    showCubeTimer: false,
                    successMessage: 'Sales campaign published to D365 successfully',
                });
                refetchSalesCampaigns();
            })
            .catch((err) => {
                showSnackbar(err);
            })
            .finally(() => {
                setPublishLoading(false);
                setShowPublishDialog(false);
            });
    }, [publish, salesCampaignId, showSnackbar, refetchSalesCampaigns]);

    useKeyboardShortcut({
        shortcutName: 'EDIT/VIEW',
        callback: (event) => {
            if (salesCampaign) {
                event.preventDefault();
                event.stopPropagation();
                handleEditSalesCampaign(salesCampaign);
            }
        },
        disabled: !salesCampaign || loading || isSaving || storefrontTypesLoading,
    });

    useSaveShortcut(saveChanges, !hasUnsavedChanges);
    useDiscardShortcut(discardChanges, !hasUnsavedChanges);

    return (
        <Stack
            sx={{
                overflow: 'auto',
                height: '100%',
            }}
        >
            <Stack
                sx={{
                    gap: '1rem',
                    height: '100%',
                }}
            >
                <Box>
                    <RouterLink
                        to={{
                            pathname: '/sales-campaigns',
                            search: location.search,
                        }}
                    >
                        <Button variant="text" startIcon={<ArrowBack />}>
                            Back to campaigns list
                        </Button>
                    </RouterLink>
                </Box>

                <HeaderBox>
                    {!salesCampaign && <CircularProgress />}
                    {salesCampaign && (
                        <>
                            <HeaderBoxItem sx={{ gridTemplateColumns: '1fr' }}>
                                <Stack
                                    sx={{
                                        alignItems: 'flex-end',
                                    }}
                                >
                                    <IconButton size={'small'} onClick={() => handleEditSalesCampaign(salesCampaign)}>
                                        <Edit fontSize={'small'} color={'secondary'} />
                                    </IconButton>
                                </Stack>
                            </HeaderBoxItem>
                            {
                                <HeaderBoxItem>
                                    <TooltipButton
                                        disabled={publishLoading || hasUnsavedChanges}
                                        variant="outlined"
                                        tooltip={
                                            hasUnsavedChanges ? 'Please save changes before publishing' : undefined
                                        }
                                        onClick={() => setShowPublishDialog(true)}
                                        loading={publishLoading}
                                    >
                                        Publish to D365
                                    </TooltipButton>
                                </HeaderBoxItem>
                            }
                            <HeaderBoxItem>
                                <Button onClick={showAuditLog} variant="text" color="primary" disabled={loading}>
                                    Audit Log
                                </Button>
                            </HeaderBoxItem>
                            <HeaderBoxItem>
                                <Typography>Campaign:</Typography>
                                <Typography>{salesCampaign?.name}</Typography>
                            </HeaderBoxItem>
                            <HeaderBoxItem>
                                <Typography>Start:</Typography>
                                <Typography>{salesCampaign?.startDate}</Typography>
                            </HeaderBoxItem>
                            <HeaderBoxItem>
                                <Typography>End:</Typography>
                                <Typography>{salesCampaign?.endDate}</Typography>
                            </HeaderBoxItem>
                            <HeaderBoxItem>
                                <Typography>Duration:</Typography>
                                <Typography>
                                    {differenceInDays(
                                        parseISO(salesCampaign?.endDate),
                                        parseISO(salesCampaign?.startDate)
                                    )}{' '}
                                    days
                                </Typography>
                            </HeaderBoxItem>
                            <HeaderBoxItem>
                                <Typography>Last published:</Typography>
                                <Typography>
                                    {salesCampaign?.lastPublished
                                        ? formatStringDateTime(salesCampaign?.lastPublished)
                                        : 'never'}
                                </Typography>
                            </HeaderBoxItem>
                        </>
                    )}
                    <HeaderBoxItem
                        sx={{
                            marginLeft: 'auto',
                            maxWidth: 150,
                        }}
                    >
                        {brandNumber && <BrandImage productLineNumber={brandNumber} />}
                    </HeaderBoxItem>
                </HeaderBox>
                <FullScreenContainer fullscreen={fullscreen}>
                    <Stack
                        direction="row"
                        sx={{
                            justifyContent: 'flex-end',
                            marginBottom: 6,
                        }}
                    >
                        <Stack
                            sx={{
                                marginRight: 'auto',
                                justifyContent: 'center',
                            }}
                        >
                            <Heading>Sales campaign planning</Heading>
                        </Stack>
                        <ActionButtons
                            hasUnsavedChanges={hasUnsavedChanges}
                            onDiscard={discardChanges}
                            onSave={saveChanges}
                            onSetInheritance={saveChanges}
                            onReset={saveChanges}
                            saveLoading={isSaving}
                            loading={loading || isSaving}
                            hideAuditLogButton
                        >
                            <IconButton color="primary" onClick={() => setFullscreen((prev) => !prev)}>
                                {fullscreen ? (
                                    <Tooltip title="Close fullscreen">
                                        <FullscreenExit />
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Open fullscreen">
                                        <Fullscreen />
                                    </Tooltip>
                                )}
                            </IconButton>
                            <SettingsDropdown gridApi={gridApi} module={Module.SalesCampaignsPlanning} />
                        </ActionButtons>
                    </Stack>

                    {error && <Typography color="error">{error.message}</Typography>}

                    <TableWrapper
                        className="ag-theme-alpine-custom-compact small-font"
                        sx={{
                            flex: 1,
                            height: `calc(100% - 64px)`,
                        }}
                        hasUnsavedChanges={hasUnsavedChanges}
                    >
                        <CachedImageProvider>
                            <DataTable
                                {...SumBold}
                                loading={loading}
                                defaultColDef={defaultColDef}
                                onGridReady={handleGridReady}
                                columns={sortedColumns}
                                rows={simulationRows}
                                isNumberTable={false}
                                autoSizeToFit={false}
                                suppressAutoSize={true}
                                onCellValuesChanged={handleCellValuesChanged}
                                domLayout="normal"
                                onCellKeyDown={handleCellKeyDown}
                                gridOptions={{
                                    statusBar: {
                                        statusPanels: [
                                            {
                                                statusPanel: CountStatusBar,
                                                align: 'right',
                                            },
                                        ],
                                    },
                                }}
                                sideBar={{
                                    position: 'left',
                                    toolPanels: [
                                        {
                                            id: 'filters',
                                            labelDefault: 'Filters',
                                            labelKey: 'filters',
                                            iconKey: 'filter',
                                            toolPanel: 'agFiltersToolPanel',
                                            toolPanelParams: {
                                                suppressExpandAll: false,
                                            },
                                        },
                                        {
                                            id: 'columns',
                                            labelDefault: 'Columns',
                                            labelKey: 'columns',
                                            iconKey: 'columns',
                                            toolPanel: 'agColumnsToolPanel',
                                            toolPanelParams: {
                                                suppressExpandAll: false,
                                            },
                                        },
                                    ],
                                }}
                                grandTotalRow="bottom"
                            />
                        </CachedImageProvider>
                    </TableWrapper>
                </FullScreenContainer>

                <StyleTimeline
                    header={<Heading>Style timeline</Heading>}
                    disableGlobalPlanning={true}
                    disableStorefrontTypeSelect={true}
                    disableActionButtons={true}
                    storefrontType={salesCampaign?.storefrontType.name}
                />
            </Stack>
            {editableSalesCampaign && storefrontTypes && (
                <SalesCampaignDialog
                    open={!!editableSalesCampaign}
                    defaultValue={editableSalesCampaign}
                    storefrontTypes={storefrontTypes}
                    onClose={() => setEditableSalesCampaign(null)}
                    isEditing
                />
            )}
            {showPublishDialog && (
                <Dialog open={showPublishDialog} onClose={() => !publishLoading && setShowPublishDialog(false)}>
                    <DialogTitle>Are you sure you want to publish to D365?</DialogTitle>
                    <DialogContent>
                        <Stack spacing={4}>
                            <Typography>
                                Click confirm to publish the sales campaign. This action cannot be undone.
                            </Typography>
                            {lastPublishedDate && (
                                <Alert severity="warning">
                                    This campaign has already been published, this may create unintended results as D365
                                    ignores duplicates. Please ensure that you remove existing discounts in D365 first
                                    for this campaign, otherwise they will not be published correctly.
                                </Alert>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="outlined"
                            disabled={publishLoading}
                            onClick={() => setShowPublishDialog(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="warning"
                            variant="contained"
                            onClick={handlePublish}
                            disabled={publishLoading}
                            loading={publishLoading}
                        >
                            <Stack
                                direction="row"
                                sx={{
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Warning />
                                <Typography>Confirm</Typography>
                            </Stack>
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Stack>
    );
};

const HeaderBox = styled(Stack)`
  flex-direction: row;
  flex-wrap: wrap;
  box-sizing: border-box;
  width: 100%;
  gap: 2rem;
  background-color: #fff;
  padding: 1.5rem;
  border-radius: 0.25rem;
`;

const HeaderBoxItem = styled(Box)`
  display: flex;
  gap: 0.5rem;
  align-items: center;

  & > *:first-of-type {
    font-weight: 600;
  }
`;
